import { Pipe, PipeTransform } from '@angular/core';

export const CATEGORY_PATH_SEPARATOR = ' > ';

export interface CategoryTreeRow {
  category: any;
  depth: number;
  // "Konami > CPU"; the plain name for a root category.
  path: string;
  hasChildren: boolean;
}

/**
 * Orders a flat category list as a tree: each parent followed by its
 * sub-categories, siblings sorted by `siblingOrder`. A category whose parent
 * is empty or not in the list is treated as a root, and the walk is guarded,
 * so bad data degrades to a flat list instead of looping.
 */
export function buildCategoryTree(
  categories: any[],
  siblingOrder: 'name' | 'position' | ((a: any, b: any) => number) = 'name'
): CategoryTreeRow[] {
  const list = categories || [];
  const byID = new Map<number, any>();
  list.forEach(c => byID.set(+c.prdCatID, c));

  const parentOf = (c: any): number => {
    const parentID = +(c.prdCatParentID || 0);
    return parentID > 0 && parentID !== +c.prdCatID && byID.has(parentID) ? parentID : 0;
  };

  const children = new Map<number, any[]>();
  list.forEach(c => {
    const parentID = parentOf(c);
    if (!children.has(parentID)) {
      children.set(parentID, []);
    }
    children.get(parentID).push(c);
  });

  const compare = typeof siblingOrder === 'function'
    ? siblingOrder
    : siblingOrder === 'position'
      ? (a: any, b: any) => (a.prdCatPos || 0) - (b.prdCatPos || 0)
      : (a: any, b: any) => (a.prdCatNameEn || '').toLowerCase().localeCompare((b.prdCatNameEn || '').toLowerCase());
  children.forEach(group => group.sort(compare));

  const rows: CategoryTreeRow[] = [];
  const seen = new Set<number>();
  const walk = (parentID: number, depth: number, prefix: string) => {
    (children.get(parentID) || []).forEach(c => {
      const id = +c.prdCatID;
      if (seen.has(id)) {
        return;
      }
      seen.add(id);
      const path = prefix + (c.prdCatNameEn || '');
      rows.push({ category: c, depth, path, hasChildren: (children.get(id) || []).length > 0 });
      walk(id, depth + 1, path + CATEGORY_PATH_SEPARATOR);
    });
  };
  walk(0, 0, '');

  return rows;
}

/**
 * For category pickers: `*ngFor="let c of categories | categoryTree"` gives
 * the categories parents-first, each with its full path in `prdCatPath`.
 * While no category is nested the list comes back untouched, in the order
 * the caller already had.
 */
@Pipe({ name: 'categoryTree' })
export class CategoryTreePipe implements PipeTransform {
  transform(categories: any[]): any[] {
    if (!categories || !categories.some(c => +(c?.prdCatParentID || 0) > 0)) {
      return categories;
    }

    return buildCategoryTree(categories).map(row => ({ ...row.category, prdCatPath: row.path, prdCatDepth: row.depth }));
  }
}
